import { AlfaIcon, SberIcon, TinkoffPayIcon, VtbPayIcon } from '@p2p/assets/svg';
import { cacheRubTjsRate } from '@p2p/pages/Redirect/deeplink/dcRate';
import {
  generateVtbDeepLink,
  isDushanbeSberDeeplink,
} from '@p2p/pages/Redirect/deeplink/deeplink.utils';
import { copyToClipboardBaseFlow } from '@p2p/pages/Redirect/RedirectPage.utils';
import { TGetOrderResponse, useGetMerchantOrderQuery, useGetRubTjsRateQuery } from '@p2p/services';
import classNames from 'classnames';
import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useRedirectLoggerFlow } from '../../../../Redirect/useRedirectLoggerFlow';
import { getShortPathToRedirect } from '../Sber.utils';
import { useDeepLink } from '../useDeepLink';
import styles from './DeepLinkActionPay.module.scss';

const icons = {
  alfa: AlfaIcon,
  sber: SberIcon,
  tbank: TinkoffPayIcon,
  vtb: VtbPayIcon,
};

export const DeepLinkActionPay = () => {
  const { id: orderId, bankId } = useParams<{ id: string; bankId: string }>();
  const { data: order } = useGetMerchantOrderQuery(orderId || '', { skip: !orderId });

  // P2P-2414/P2P-2744: заранее тянем курс RUB/TJS и кладём в кеш, чтобы
  // redirect.html построил DC-Сбер deeplink (C2C и SBP) синхронно (иначе
  // банк не успевает открыться).
  const isDcSber = isDushanbeSberDeeplink(order);
  const { data: rubTjsRate } = useGetRubTjsRateQuery(undefined, { skip: !isDcSber });
  useEffect(() => {
    if (rubTjsRate?.rate != null) {
      cacheRubTjsRate(rubTjsRate.rate);
    }
  }, [rubTjsRate]);
  const {
    isSber,
    isSberBabilon,
    isTinkoff,
    isVtb,
    isAlfa,
    isAlfaTajikistan,
    isPsb,
    isHalvaDushanbe,
    isIOS,
    isRmFlow,
    rmFlowLink,
  } = useDeepLink({
    order,
  });
  const { loggerBtnClick } = useRedirectLoggerFlow({ order });
  const clientBankCode = order?.client_bank_code?.toLowerCase();
  const isHasIcon = Object.keys(icons).includes(clientBankCode || '');
  const getIcon = () => {
    if (isRmFlow) {
      return icons.tbank;
    }
    return clientBankCode && isHasIcon ? icons[clientBankCode as keyof typeof icons] : null;
  };
  const Icon = getIcon();

  const pathToRedirect = useMemo(() => {
    if (isRmFlow) {
      return rmFlowLink;
    }

    if (isVtb && isIOS) {
      // if isVtb and isIOS, return the https vtb deep link P2P-2025
      return generateVtbDeepLink(order as TGetOrderResponse, 'https');
    }

    return getShortPathToRedirect(orderId || '', Number(bankId));
  }, [bankId, isIOS, isRmFlow, isVtb, order, orderId, rmFlowLink]);

  const handleClick = () => {
    loggerBtnClick();

    if (!isAlfa) {
      copyToClipboardBaseFlow(order);
    }
  };

  return (
    <Link
      className={classNames(styles.container, {
        [styles.tinkoffContainer]: isTinkoff || isRmFlow,
        [styles.vtbContainer]: isVtb && !isRmFlow,
        [styles.alfaContainer]: isAlfa && !isRmFlow,
      })}
      onClick={handleClick}
      to={pathToRedirect}
    >
      {isPsb ? (
        // P2P-2594: у ПСБ нет иконки/варианта «...Pay» — отдельный текст кнопки по ТЗ.
        <span className={styles.leftPart}>Оплатить через ПСБ</span>
      ) : isHalvaDushanbe ? (
        // P2P-2801: у Халвы (Совкомбанк) нет иконки/варианта «...Pay» — отдельный
        // текст кнопки, как у ПСБ выше.
        <span className={styles.leftPart}>Оплатить через Халву</span>
      ) : isSberBabilon ? (
        // P2P-2612: для Babilon-M это не настоящий SberPay (статичная ссылка на
        // приложение, а не p2p-перевод) — текст кнопки по ТЗ, без бренда «...Pay».
        <span className={styles.leftPart}>Оплатить через Сбер</span>
      ) : isAlfaTajikistan ? (
        // P2P-2663: Alfa Pay C2C на банки Таджикистана — статичная веб-ссылка
        // (не настоящий AlfaPay-переход по SBP), текст кнопки по ТЗ.
        <span className={styles.leftPart}>Оплатить через Альфа Банк</span>
      ) : (
        <>
          <span className={styles.leftPart}>Оплатить с</span>
          {Icon && <Icon />}
          {(isSber || isVtb || isAlfa) && !isRmFlow && (
            <span className={styles.rightPart}>Pay</span>
          )}
        </>
      )}
    </Link>
  );
};
